import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Search, Filter, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface LogEntry {
  id: string;
  action: string;
  details: any;
  admin_email: string;
  target_user_email?: string;
  created_at: string;
}

const AdminLogsPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.target_user_email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = filterAction === 'all' || log.action === filterAction;

    return matchesSearch && matchesAction;
  });

  const exportLogs = () => {
    const csv = [
      ['Ação', 'Admin', 'Usuário Alvo', 'Data'],
      ...filteredLogs.map(log => [
        log.action,
        log.admin_email,
        log.target_user_email || '-',
        format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-logs-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
  };

  const getActionBadgeColor = (action: string) => {
    if (action.includes('update') || action.includes('edit')) return 'bg-blue-500';
    if (action.includes('delete') || action.includes('remove')) return 'bg-red-500';
    if (action.includes('create') || action.includes('add')) return 'bg-green-500';
    return 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Logs de Auditoria</h1>
      </div>

      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar em logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">Todas as Ações</option>
            <option value="update_plan">Atualização de Plano</option>
            <option value="update_user">Atualização de Usuário</option>
            <option value="delete_user">Exclusão de Usuário</option>
            <option value="send_notification">Envio de Notificação</option>
          </select>

          <button
            onClick={exportLogs}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Ação</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Admin</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Usuário Alvo</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Data/Hora</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs text-white ${getActionBadgeColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-300">{log.admin_email}</td>
                  <td className="py-3 px-4 text-slate-300">{log.target_user_email || '-'}</td>
                  <td className="py-3 px-4 text-slate-300">
                    {format(new Date(log.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-sm">
                    {log.details ? JSON.stringify(log.details).substring(0, 50) + '...' : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              Nenhum log encontrado
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogsPage;
